import { cn } from "@common/lib/utils"

const HALF_PIE = "M7 7V3.8A3.2 3.2 0 0 1 7 10.2Z"

const THREE_QUARTER_PIE = "M7 7V3.8A3.2 3.2 0 1 1 3.8 7Z"

const CLOCK = "M7 4.2V7l2 1.4"

export const CHECK = "M4.3 7.2l1.8 1.8 3.6-4"

export const CROSS = "M4.8 4.8l4.4 4.4M9.2 4.8L4.8 9.2"

const SLASH = "M4.2 9.8l5.6-5.6"

const DASH = "M4.5 7h5"

const PAUSE = "M5.6 4.8v4.4M8.4 4.8v4.4"

const SENT = "M4.3 7h5.2M7.4 4.6L9.8 7l-2.4 2.4"

export type StatusKind =
  | "draft"
  | "pending"
  | "in_progress"
  | "almost_done"
  | "waiting"
  | "paused"
  | "done"
  | "verified"
  | "sent"
  | "rejected"
  | "failed"
  | "cancelled"
  | "skipped"

export const STATUS_TONE: Record<StatusKind, string> = {
  draft: "text-muted-foreground",
  pending: "text-semantic-warning",
  in_progress: "text-semantic-warning",
  almost_done: "text-semantic-warning",
  waiting: "text-semantic-warning",
  paused: "text-semantic-warning",
  done: "text-semantic-success",
  verified: "text-semantic-success",
  sent: "text-semantic-success",
  rejected: "text-semantic-error",
  failed: "text-semantic-error",
  cancelled: "text-muted-foreground",
  skipped: "text-muted-foreground",
}

export function StatusGlyph({
  kind,
  label,
}: {
  kind: StatusKind
  label: string
}): React.ReactElement {
  return (
    <StatusSvg label={label} tone={STATUS_TONE[kind]}>
      <KindGlyph kind={kind} />
    </StatusSvg>
  )
}

function KindGlyph({ kind }: { kind: StatusKind }): React.ReactElement {
  switch (kind) {
    case "draft":
      return <Ring dashed />
    case "pending":
      return <Dot />
    case "in_progress":
      return <Pie d={HALF_PIE} />
    case "almost_done":
      return <Pie d={THREE_QUARTER_PIE} />
    case "waiting":
      return <Hand d={CLOCK} />
    case "paused":
      return <Hand d={PAUSE} />
    case "done":
      return <Hand d={CHECK} />
    case "verified":
      return <Disc mark={CHECK} />
    case "sent":
      return <Disc mark={SENT} />
    case "rejected":
      return <Hand d={CROSS} />
    case "failed":
      return <Disc mark={CROSS} />
    case "cancelled":
      return <Hand d={SLASH} />
    case "skipped":
      return <Hand d={DASH} />
  }
}

export function StatusSvg({
  label,
  tone,
  children,
}: {
  label: string
  tone: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox="0 0 14 14"
      fill="none"
      className={cn("size-3.5 shrink-0", tone)}
    >
      <title>{label}</title>
      {children}
    </svg>
  )
}

export function Ring({
  dashed = false,
}: {
  dashed?: boolean
}): React.ReactElement {
  return (
    <circle
      cx={7}
      cy={7}
      r={5.5}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeDasharray={dashed ? "2 2" : undefined}
    />
  )
}

export function Dot(): React.ReactElement {
  return (
    <>
      <Ring />
      <circle cx={7} cy={7} r={2} fill="currentColor" />
    </>
  )
}

function Pie({ d }: { d: string }): React.ReactElement {
  return (
    <>
      <Ring />
      <path d={d} fill="currentColor" />
    </>
  )
}

function Hand({ d }: { d: string }): React.ReactElement {
  return (
    <>
      <Ring />
      <Mark d={d} className="stroke-current" />
    </>
  )
}

function Disc({ mark }: { mark: string }): React.ReactElement {
  return (
    <>
      <circle cx={7} cy={7} r={6} fill="currentColor" />
      <Mark d={mark} className="stroke-background" />
    </>
  )
}

export function Square({ mark }: { mark?: string }): React.ReactElement {
  if (!mark) {
    return (
      <rect
        x={2}
        y={2}
        width={10}
        height={10}
        rx={2.5}
        stroke="currentColor"
        strokeWidth={1.5}
      />
    )
  }
  return (
    <>
      <rect
        x={1.5}
        y={1.5}
        width={11}
        height={11}
        rx={2.5}
        fill="currentColor"
      />
      <Mark d={mark} className="stroke-background" />
    </>
  )
}

function Mark({
  d,
  className,
}: {
  d: string
  className: string
}): React.ReactElement {
  return (
    <path
      d={d}
      className={className}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  )
}
